import {
  Autocomplete,
  Button,
  Chip,
  Fade,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DbMetadataNamingRuleInfos } from "../../../../Core/Api/Types/db-metadata-naming-rule-infos";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps, accordionSummaryClasses } from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import AssetTypeChip from "../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { DbMetadataFieldInfos } from "../../../../Core/Api/Types/db-metadata-field-infos";
import { AddOutlined, DeleteForeverOutlined, InfoOutlined, SaveOutlined } from "@mui/icons-material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CodeBox from "../../../../Components/Misc/CodeBox/code-box";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import { generateRulesFromTokens, TokenizedNamingRule } from "./commons";
import SingleRuleLine from "./single-rule-line";
import SingleLineNoEdit from "./single-rule-line-no-edit";
import ExampleNamesList from "./example-name-list";
import { cacheService } from "../../../../Core/Api/services";

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&::before": {
      display: "none",
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />} {...props} />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: "rotate(90deg)",
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

interface NamingRuleCardProps {
  namingRule: DbMetadataNamingRuleInfos;
  handleChangeExpanded: (panelIndex: number) => (event: React.SyntheticEvent, newExpanded: boolean) => void;
  expanded: number;
  onChange: (e: any, tokenIndex: number, field: string, ruleIndex: number, nrIndex: number) => void;
  metadataFields: DbMetadataFieldInfos[];
  tokenizedNamingRule: TokenizedNamingRule;
  nrIndex: number;
  onDeleteRule: (nrIndex: number, ruleIndex: number) => void;
  onDeleteToken: (nrIndex: number, ruleIndex: number, tokenIndex: number) => void;
  onAddRule: (nrIndex: number, type: "2-conditions" | "condition" | "value") => void;
  onAddToken: (nrIndex: number, ruleIndex: number, type: "2-conditions" | "condition" | "value") => void;
  onDeleteNr: (e: any, nrIndex: number) => void;
  onSwapRule: (nrIndex: number, ruleIndex: number, direction: "up" | "down") => void;
  onCopyRule: (nrIndex: number, ruleIndex: number) => void;
  onCopyToken: (nrIndex: number, ruleIndex: number, tokenIndex: number) => void;
  editingRuleIndex: number;
  handleEditRule: (index: number) => void;
  unsavedNrIndexes: number[];
  handleSave: (nrIndex: number, e: any) => void;
  mode: "view" | "edit";
  handleToggleMode: (e: any) => void;
  handleConfigClick: (e: any) => void;
  handleContentSubTypeChange: (e: any, value: string | null) => void;
  handlePasteRawNr: (e: any) => void;
  changeRawRules: (text: string) => void;
}

const NamingRuleCard: React.FC<NamingRuleCardProps> = ({
  namingRule,
  handleChangeExpanded,
  expanded,
  onChange,
  metadataFields,
  tokenizedNamingRule,
  nrIndex,
  onAddRule,
  onDeleteRule,
  onDeleteToken,
  onAddToken,
  onDeleteNr,
  onSwapRule,
  onCopyRule,
  onCopyToken,
  editingRuleIndex,
  handleEditRule,
  unsavedNrIndexes,
  handleSave,
  mode,
  handleToggleMode,
  handleConfigClick,
  handleContentSubTypeChange,
  handlePasteRawNr,
  changeRawRules,
}) => {
  const { t } = useTranslation();

  const id = nrIndex;
  const unsaved = unsavedNrIndexes.includes(nrIndex);
  const contentSubTypeMeta = cacheService.GetMetadata().find((x) => x.tagName === "contentSubType");
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (expanded !== id) {
      setAnchorEl(null);
      setOpen(false);
    }
  }, [expanded]);

  const handleClickInfos = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };
  const handlePasteClick = () => {
    navigator.clipboard.readText().then((text) => changeRawRules(text));
  };
  if (id < 0) return <></>;
  return (
    <>
      <Accordion expanded={expanded === id} onChange={handleChangeExpanded(id)}>
        <AccordionSummary aria-controls={`${id}d-content`} id="panel1d-header">
          <Stack width={"100%"} direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <Typography color={unsaved ? "text.primary" : "text.tier"} ml={1}>
              {namingRule.objectType}
            </Typography>

            <Stack alignItems={"center"} direction={"row"} spacing={1}>
              <Popper
                // Note: The following zIndex style is specifically for documentation purposes and may not be necessary in your application.
                sx={{ zIndex: 1200 }}
                open={open}
                anchorEl={anchorEl}
                placement={"bottom"}
                transition
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={350}>
                    <Paper sx={{ borderRadius: 1 }}>
                      <Stack p={2} width={"600px"} spacing={1} direction={"column"}>
                        <Stack
                          alignItems={"center"}
                          p={0.5}
                          width={"100%"}
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="h6" color={"text.secondary"}>
                            {t("tooltip:title.namingRuleId")}
                          </Typography>
                          <Typography variant="body2">{namingRule.id}</Typography>
                        </Stack>
                        <Stack p={0.5} width={"100M"} direction={"row"} justifyContent={"space-between"}>
                          <Typography variant="h6" color={"text.secondary"}>
                            {t("tooltip:title.rulesCount")}
                          </Typography>
                          <Typography variant="body2">{tokenizedNamingRule.rules.length}</Typography>
                        </Stack>
                        <Stack
                          alignItems={"center"}
                          p={0.5}
                          width={"100M"}
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="h6" color={"text.secondary"}>
                            {t("tooltip:title.namingRuleForType")}
                          </Typography>
                          <Typography variant="body2">{namingRule.objectType}</Typography>
                        </Stack>
                        <Stack
                          alignItems={"center"}
                          p={0.5}
                          width={"100M"}
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="h6" color={"text.secondary"}>
                            {t("tooltip:title.namingRuleForSubType")}
                          </Typography>
                          <Typography color={namingRule.contentSubType ? "text.primary" : "text.tier"} variant="body2">
                            {namingRule.contentSubType ?? t("modal:body.noSubContent")}
                          </Typography>
                        </Stack>
                        <Stack
                          alignItems={"center"}
                          p={0.5}
                          width={"100M"}
                          direction={"row"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="h6" color={"text.secondary"}>
                            {t("tooltip:title.namingRuleContext")}
                          </Typography>
                          <Typography variant="body2">{namingRule.context}</Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Fade>
                )}
              </Popper>
              {expanded === id && (
                <IconButton size="small" onClick={handleClickInfos}>
                  <InfoOutlined fontSize="small" color="info" />
                </IconButton>
              )}
              {expanded === id && (
                <IconButton size="small" onClick={handleToggleMode}>
                  {mode === "view" ? (
                    <EditNoteOutlinedIcon color="primary" />
                  ) : (
                    <VisibilityOutlinedIcon color="primary" />
                  )}
                </IconButton>
              )}
              {expanded === id && (
                <IconButton size="small" onClick={(e) => onDeleteNr(e, nrIndex)}>
                  <DeleteForeverOutlined color="error" />
                </IconButton>
              )}
              {expanded === id && (
                <IconButton disabled={!unsaved} size="small" onClick={(e) => handleSave(nrIndex, e)}>
                  <SaveOutlined color={unsaved ? "success" : "disabled"} />
                </IconButton>
              )}
              {expanded === id && (
                <Autocomplete
                  sx={{
                    ".MuiInputBase-root": {
                      margin: "0px !important",
                    },
                  }}
                  onChange={handleContentSubTypeChange}
                  renderInput={(params) => (
                    <TextField
                      placeholder={t("field:placeholder.specifySubContent")}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      sx={{ width: "260px" }}
                      {...params}
                    />
                  )}
                  options={contentSubTypeMeta?.values?.map((x) => x.value ?? "") ?? []}
                  value={namingRule.contentSubType}
                  getOptionLabel={(option) => option}
                />
              )}
              <AssetTypeChip type={namingRule.objectType ?? ObjectType.Unknown} />
              <Chip label={namingRule.context} />
            </Stack>
          </Stack>
        </AccordionSummary>
        {expanded === id && (
          <AccordionDetails sx={{ padding: "0px !important", paddingTop: "8px !important" }}>
            {mode === "edit" ? (
              <Stack mb={1} spacing={1} direction="column">
                {tokenizedNamingRule?.rules.map((x, i) =>
                  editingRuleIndex === i ? (
                    <SingleRuleLine
                      key={i}
                      ruleIndex={i}
                      tokenizedNamingRule={x}
                      metadataFields={metadataFields}
                      onChange={onChange}
                      nrIndex={nrIndex}
                      onDeleteRule={onDeleteRule}
                      onDeleteToken={onDeleteToken}
                      onAddToken={onAddToken}
                      onSwapRule={onSwapRule}
                      rulesCount={tokenizedNamingRule.rules.length}
                      onCopyRule={onCopyRule}
                      onCopyToken={onCopyToken}
                      handleEditRule={handleEditRule}
                    />
                  ) : (
                    <SingleLineNoEdit handleEditRule={handleEditRule} i={i} x={x} key={i} />
                  )
                )}
                <Stack spacing={1} direction={"row"} display={"flex"}>
                  <Button
                    onClick={() => onAddRule(nrIndex, "value")}
                    color="primary"
                    variant="outlined"
                    fullWidth
                    startIcon={<AddOutlined />}
                  >
                    {t("common:add")} value
                  </Button>
                  <Button
                    onClick={() => onAddRule(nrIndex, "condition")}
                    color="primary"
                    variant="outlined"
                    fullWidth
                    startIcon={<AddOutlined />}
                  >
                    {t("common:add")} condtion
                  </Button>
                  <Button
                    onClick={() => onAddRule(nrIndex, "2-conditions")}
                    color="primary"
                    variant="outlined"
                    fullWidth
                    startIcon={<AddOutlined />}
                  >
                    {t("common:add")} double condition
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack mb={2} direction="column">
                <ExampleNamesList
                  handleConfigClick={handleConfigClick}
                  metadataFields={metadataFields}
                  tokenizedNamingRule={tokenizedNamingRule}
                />
                <Stack direction="column" px={1} spacing={1}>
                  <FormSection
                    icon={<InfoOutlined fontSize="small" />}
                    title={t("menu:title.ruleDetails")}
                    size="small"
                    defaultExpanded
                  >
                    <CodeBox
                      onClickPaste={handlePasteClick}
                      onClickEdit={handlePasteRawNr}
                      copyEnable
                      text={generateRulesFromTokens(tokenizedNamingRule.rules)}
                    />
                  </FormSection>
                </Stack>
              </Stack>
            )}
          </AccordionDetails>
        )}
      </Accordion>
    </>
  );
};
export default NamingRuleCard;
